import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  Users, 
  Shield,
  Loader2,
  GitBranch,
  Rocket,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MasterAdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [versionChannels, setVersionChannels] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const channelsRes = await fetch(`${API_URL}/api/version/channels`);
      const channelsData = await channelsRes.json();
      setVersionChannels(channelsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Shield className="h-8 w-8 text-purple-500" />
            Master Admin Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage version channels and white-label licenses
          </p>
        </div>
      </div>

      {/* Version Control Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-purple-500" />
            Version Channels
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* SaaS Channel */}
            <div className="p-4 border border-border rounded-xl bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <Rocket className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="font-semibold">SaaS (FlowFeed)</p>
                  <p className="text-xs text-muted-foreground">Latest features</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Badge className="bg-blue-500">
                  v{versionChannels?.channels?.saas?.version || '1.0.0'}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {versionChannels?.channels?.saas?.release_date || '-'}
                </span>
              </div>
            </div>

            {/* White-Label Channel */}
            <div className="p-4 border border-border rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 bg-emerald-500 rounded-lg">
                  <Building2 className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="font-semibold">White-Label</p>
                  <p className="text-xs text-muted-foreground">Stable for clients</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Badge className="bg-emerald-500">
                  v{versionChannels?.channels?.['white-label']?.version || '1.0.0'}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {versionChannels?.channels?.['white-label']?.release_date || '-'}
                </span>
              </div>
            </div>

            {/* Beta Channel */}
            <div className="p-4 border border-border rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 bg-orange-500 rounded-lg">
                  <AlertTriangle className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="font-semibold">Beta</p>
                  <p className="text-xs text-muted-foreground">Experimental</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Badge variant="outline" className="border-orange-500 text-orange-600">
                  v{versionChannels?.channels?.beta?.version || '-'}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {versionChannels?.channels?.beta?.release_date || '-'}
                </span>
              </div>
            </div>
          </div>

          {/* Update Strategy Info */}
          <div className="mt-4 p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Update Strategy
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">SaaS-Only Update:</span> Edit <code className="bg-background px-1.5 py-0.5 rounded text-xs">version.json</code> → Update <code className="text-xs">channels.saas.version</code>
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">White-Label Release:</span> Edit <code className="bg-background px-1.5 py-0.5 rounded text-xs">version.json</code> → Update <code className="text-xs">channels.white-label.version</code>
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              This allows you to test new features in FlowFeed SaaS before releasing to white-label clients.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => navigate('/company-management')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-500/10 rounded-xl">
                <Building2 className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <h3 className="font-semibold">Manage Companies</h3>
                <p className="text-sm text-muted-foreground">Add, edit, or remove your company branches</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => navigate('/users')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-500/10 rounded-xl">
                <Users className="h-6 w-6 text-purple-500" />
              </div>
              <div>
                <h3 className="font-semibold">User Management</h3>
                <p className="text-sm text-muted-foreground">Manage users and their company access</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MasterAdminDashboard;
